/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { CancelJobResponse } from '../models/CancelJobResponse';
import type { FileDownloadUrlResponse } from '../models/FileDownloadUrlResponse';
import type { FlatFileImportJobRead } from '../models/FlatFileImportJobRead';
import type { FlatFileImportJobSummary } from '../models/FlatFileImportJobSummary';
import type { ImportJobTypeEnum } from '../models/ImportJobTypeEnum';
import type { StartCashflowImportRequest } from '../models/StartCashflowImportRequest';
import type { StartEntityImportRequest } from '../models/StartEntityImportRequest';
import type { StartFieldImportRequest } from '../models/StartFieldImportRequest';
import type { StartFieldValueImportRequest } from '../models/StartFieldValueImportRequest';
import type { StartImportJobResponse } from '../models/StartImportJobResponse';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class BackgroundJobsService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * Start Entity Import
     * Start an async entity import job.
     *
     * The job will:
     * 1. Create a FlatFileImportJob record
     * 2. Enqueue a background task to process the import
     * 3. Return immediately with job_id
     *
     * The background task will:
     * 1. Process the import using OrchestrationService.bulk_import_entities_from_file
     * 2. Update the job record with results
     * 3. Send WebSocket notification with results
     * @param requestBody
     * @returns StartImportJobResponse Successful Response
     * @throws ApiError
     */
    public startEntityImport(
        requestBody: StartEntityImportRequest,
    ): CancelablePromise<StartImportJobResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/background-jobs/entity-import/start',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Start Cashflow Import
     * Start an async cashflow import job.
     *
     * The job will:
     * 1. Create a FlatFileImportJob record
     * 2. Enqueue a background task to process the import
     * 3. Return immediately with job_id
     * @param requestBody
     * @returns StartImportJobResponse Successful Response
     * @throws ApiError
     */
    public startCashflowImport(
        requestBody: StartCashflowImportRequest,
    ): CancelablePromise<StartImportJobResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/background-jobs/cashflow-import/start',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Start Field Import
     * Start an async field/formula import job.
     *
     * The job will:
     * 1. Create a FlatFileImportJob record
     * 2. Enqueue a background task to process the import
     * 3. Return immediately with job_id
     * @param requestBody
     * @returns StartImportJobResponse Successful Response
     * @throws ApiError
     */
    public startFieldImport(
        requestBody: StartFieldImportRequest,
    ): CancelablePromise<StartImportJobResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/background-jobs/field-import/start',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Start Field Value Import
     * Start an async field value import job.
     *
     * The job will:
     * 1. Create a FlatFileImportJob record
     * 2. Enqueue a background task to process the import
     * 3. Return immediately with job_id
     *
     * The background task will:
     * 1. Process the import using FieldValueImporterService.import_field_values
     * 2. Update the job record with results
     * 3. Send WebSocket notification with results
     * @param requestBody
     * @returns StartImportJobResponse Successful Response
     * @throws ApiError
     */
    public startFieldValueImport(
        requestBody: StartFieldValueImportRequest,
    ): CancelablePromise<StartImportJobResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/background-jobs/field-value-import/start',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Import Job
     * Get details of a specific import job including full results.
     * Used for "View Results" action.
     * @param jobId
     * @returns FlatFileImportJobRead Successful Response
     * @throws ApiError
     */
    public getImportJob(
        jobId: string,
    ): CancelablePromise<FlatFileImportJobRead> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/background-jobs/jobs/{job_id}',
            path: {
                'job_id': jobId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Import Jobs
     * List import jobs for a given context.
     * Returns lightweight summaries without heavy input_data/result fields.
     * Used for showing import history in the ImportModal.
     * @param clusterId
     * @param jobType
     * @param namespaceId
     * @param entityId
     * @param limit
     * @returns FlatFileImportJobSummary Successful Response
     * @throws ApiError
     */
    public listImportJobs(
        clusterId: string,
        jobType?: (ImportJobTypeEnum | null),
        namespaceId?: (string | null),
        entityId?: (string | null),
        limit: number = 50,
    ): CancelablePromise<Array<FlatFileImportJobSummary>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/background-jobs/jobs',
            query: {
                'cluster_id': clusterId,
                'job_type': jobType,
                'namespace_id': namespaceId,
                'entity_id': entityId,
                'limit': limit,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Cancel Job
     * Cancel a running or stale job.
     *
     * Marks the job as FAILED with a cancellation message.
     * @param jobId
     * @returns CancelJobResponse Successful Response
     * @throws ApiError
     */
    public cancelJob(
        jobId: string,
    ): CancelablePromise<CancelJobResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/background-jobs/jobs/{job_id}/cancel',
            path: {
                'job_id': jobId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Import Job File
     * Get presigned download URL for the original import file.
     * Returns 404 if job has no file attached.
     * @param jobId
     * @returns FileDownloadUrlResponse Successful Response
     * @throws ApiError
     */
    public getImportJobFile(
        jobId: string,
    ): CancelablePromise<FileDownloadUrlResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/background-jobs/jobs/{job_id}/file',
            path: {
                'job_id': jobId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

}
